import { supabase } from '../lib/supabase';
import type { TemplateConfig } from '../types/tradingPartner';

/**
 * WDEV-142 — customer-specific defaults ("customer_defaults" profile).
 *
 * Sender-side defaults + Ship-From / Selling-Party identity, keyed by the
 * composing ACCOUNT (reports account_id, passed by the host as the `account-id`
 * attribute). This is the account dimension, NOT the JWT `customer` claim —
 * US Nameplate composes regular orders under account 1620 (sender P4307J0) and
 * Direct Ship under account 1621 (P4307J1); everything else is identical.
 *
 * Design: shared-docs/2026-07-23-editor-customer-defaults-design.md (Option B).
 *
 * The profile is OVERLAID onto the trading-partner TemplateConfig before the
 * existing applicators run, so all the tested seeding logic is reused. Merge
 * precedence (later wins), matching the design:
 *   1. partner template defaults          (receiver base)
 *   2. customer_defaults, partner NULL     (account-wide)
 *   3. customer_defaults, partner = this   (per-partner override)
 *   4. operator per-document edits         (win last, in the form)
 */

/** One party's identity as stored in customer_defaults.identity JSON. */
export interface PartyIdentity {
  name?: string;
  id?: string;
  qualifier?: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
}

/** A row from the customer_defaults table. */
export interface CustomerDefaultsRow {
  customer_code: string;
  /** trading_partners.id (UUID); NULL = applies to all of this account's partners. */
  trading_partner_id: string | null;
  /** '856' | '810' | null (= all doc types). */
  document_type: string | null;
  defaults: {
    header?: Record<string, string>;
    lineItems?: Record<string, string>;
    /**
     * Per-account Part Information lines to guarantee on every line item
     * (Natalie #4: the DS account needs a 3rd part row, Engineering Change
     * Level, that the shared Antares template does not carry). Merged into
     * prePopulateRequirements.lineItems.addPartInfoByDefault by qualifier, so
     * lines the template already defines are not duplicated. Kept here rather
     * than in the template because it must apply ONLY to the composing account
     * (1621), not to the same partner composed as regular CAT (1620).
     */
    partInfo?: { qualifier: string; label?: string; id?: string }[];
  } | null;
  /** { SF: {...}, SE: {...}, ... } keyed by EDI entity code. */
  identity: Record<string, PartyIdentity> | null;
}

/** Map the editor document type to the EDI doc code used in customer_defaults. */
function docTypeCode(documentType: 'asn' | 'invoice'): '856' | '810' {
  return documentType === 'asn' ? '856' : '810';
}

/**
 * Select the rows that apply to (docType, partnerId), ordered least→most
 * specific so the caller can apply them in order and let the most specific win.
 * Specificity: a doc-type match beats doc-type NULL; a partner match beats
 * partner NULL.
 */
function applicableRows(
  rows: CustomerDefaultsRow[],
  documentType: 'asn' | 'invoice',
  partnerId?: string,
): CustomerDefaultsRow[] {
  const code = docTypeCode(documentType);
  return rows
    .filter((r) => r.document_type == null || r.document_type === code)
    .filter((r) => r.trading_partner_id == null || (partnerId != null && r.trading_partner_id === partnerId))
    .sort((a, b) => specificity(a) - specificity(b));
}

function specificity(r: CustomerDefaultsRow): number {
  return (r.document_type != null ? 1 : 0) + (r.trading_partner_id != null ? 2 : 0);
}

/** Copy defined (non-undefined) string entries from src onto a target record. */
function assignDefined(target: Record<string, string>, src?: Record<string, string>): void {
  if (!src) return;
  for (const [k, v] of Object.entries(src)) {
    if (v !== undefined) target[k] = v;
  }
}

/**
 * Overlay the customer_defaults rows onto a TemplateConfig, returning a NEW
 * config (the input is not mutated — template_config objects are shared/reused).
 * When no rows apply, returns a structural clone unchanged.
 */
export function overlayCustomerDefaults(
  config: TemplateConfig,
  rows: CustomerDefaultsRow[],
  documentType: 'asn' | 'invoice',
  partnerId?: string,
): TemplateConfig {
  const out: TemplateConfig = structuredClone(config);
  const applicable = applicableRows(rows, documentType, partnerId);
  if (applicable.length === 0) return out;

  // Ensure the containers the merges write into exist.
  out.defaults = out.defaults ?? ({ header: {}, orders: {}, lineItems: {} } as TemplateConfig['defaults']);
  out.defaults.header = { ...(out.defaults.header ?? {}) };
  out.defaults.lineItems = { ...(out.defaults.lineItems ?? {}) };

  for (const row of applicable) {
    assignDefined(out.defaults.header as Record<string, string>, row.defaults?.header);
    assignDefined(out.defaults.lineItems as Record<string, string>, row.defaults?.lineItems);
    if (row.defaults?.partInfo?.length) applyPartInfo(out, row.defaults.partInfo);
    if (row.identity) applyIdentity(out, documentType, row.identity);
  }

  return out;
}

/**
 * Merge per-account Part Information lines into the slot the ASN/invoice
 * applicators read (prePopulateRequirements.lineItems.addPartInfoByDefault),
 * upserting by qualifier so template-defined lines are extended, not
 * duplicated. A template that used the boolean `true` form (generic "seed an
 * empty part row") is promoted to the explicit list, since the account is now
 * naming the exact rows it wants.
 */
function applyPartInfo(
  out: TemplateConfig,
  partInfo: { qualifier: string; label?: string; id?: string }[],
): void {
  const pre = (out.prePopulateRequirements = out.prePopulateRequirements ?? {});
  const lineItems = (pre.lineItems = pre.lineItems ?? {});
  const existing = Array.isArray(lineItems.addPartInfoByDefault)
    ? lineItems.addPartInfoByDefault
    : [];
  const list = [...existing];
  for (const part of partInfo) {
    upsertByKey(list, 'qualifier', part.qualifier, { ...part });
  }
  lineItems.addPartInfoByDefault = list;
}

/**
 * Inject each party in `identity` into the prePopulate slot the applicator
 * reads: ASN → orders.addNamesByDefault (keyed by entityIdentifierCode),
 * invoice → parties.addByDefault (keyed by type). Existing entries of the same
 * entity code are replaced (customer identity wins); other entities (e.g. ST)
 * are preserved.
 */
function applyIdentity(
  out: TemplateConfig,
  documentType: 'asn' | 'invoice',
  identity: Record<string, PartyIdentity>,
): void {
  const pre = (out.prePopulateRequirements = out.prePopulateRequirements ?? {});

  if (documentType === 'asn') {
    const orders = (pre.orders = pre.orders ?? { min: 1 });
    if (orders.min == null) orders.min = 1;
    const list = (orders.addNamesByDefault = orders.addNamesByDefault ?? []);
    for (const [type, party] of Object.entries(identity)) {
      upsertByKey(list, 'entityIdentifierCode', type, { entityIdentifierCode: type, ...party });
    }
  } else {
    const parties = (pre.parties = pre.parties ?? {});
    const list = (parties.addByDefault = parties.addByDefault ?? []);
    for (const [type, party] of Object.entries(identity)) {
      upsertByKey(list, 'type', type, { type, ...party });
    }
  }
}

/** Replace the entry whose `keyField` equals `keyValue`, or append if absent. */
function upsertByKey<T extends Record<string, unknown>>(
  list: T[],
  keyField: keyof T,
  keyValue: string,
  entry: T,
): void {
  const idx = list.findIndex((e) => e[keyField] === keyValue);
  if (idx >= 0) list[idx] = { ...list[idx], ...entry };
  else list.push(entry);
}

// ── Runtime store + fetch ────────────────────────────────────────────────────
// Loaded once per account (the host sets `account-id` before the operator opens
// the editor). Overlay reads the cache synchronously at document-create time.
// If the cache is not yet loaded for an account, overlay is a no-op and the
// trading-partner template defaults still apply — a safe fallback.

let _rows: CustomerDefaultsRow[] = [];
let _loadedForAccount: string | null = null;

/** The cached rows for the current account (empty until loaded). */
export function getCachedCustomerDefaults(): CustomerDefaultsRow[] {
  return _rows;
}

export function clearCustomerDefaults(): void {
  _rows = [];
  _loadedForAccount = null;
}

/**
 * Fetch and cache the customer_defaults rows for an account. Idempotent per
 * account. Fails loud on a real query error (EDI rule: no silent fallback);
 * a genuinely empty result is cached as "loaded, no rows".
 */
export async function loadCustomerDefaults(accountCode: string): Promise<CustomerDefaultsRow[]> {
  if (_loadedForAccount === accountCode) return _rows;

  const { data, error } = await supabase
    .from('customer_defaults')
    .select('customer_code,trading_partner_id,document_type,defaults,identity')
    .eq('customer_code', accountCode)
    .eq('is_active', 1);

  if (error) {
    console.error('[customerDefaults] fetch failed for account', accountCode, error);
    throw error;
  }

  _rows = (data ?? []).map(normalizeRow);
  _loadedForAccount = accountCode;
  return _rows;
}

/** MyREST may return JSON columns as strings; normalize to objects. */
function normalizeRow(raw: CustomerDefaultsRow & { defaults: unknown; identity: unknown }): CustomerDefaultsRow {
  const parse = <T>(v: unknown): T | null =>
    typeof v === 'string' ? (v ? (JSON.parse(v) as T) : null) : ((v as T) ?? null);
  return {
    customer_code: raw.customer_code,
    trading_partner_id: raw.trading_partner_id ?? null,
    document_type: raw.document_type ?? null,
    defaults: parse(raw.defaults),
    identity: parse(raw.identity),
  };
}
